/** @jsx jsx */
import { jsx } from '@emotion/react';
import { Icon, IconName, Tooltip, Typography } from '@zapier/design-system';
import { labelStyles, withModifier } from '@zapier/style-encapsulation';
import { ReactNode } from 'react';
import { Button, PressEvent } from 'react-aria-components';
import { createUniqueClassName } from '../../../utils/createUniqueClassName';

export type Props = {
  /**
   * The `Icon` component to render within the button.
   * **Note that if renderBeforeText is defined, that prop is then responsible for rendering.**
   */
  icon: IconName;
  /**
   * Indicates whether the button is collapsed down to just an icon.
   */
  isCollapsed?: boolean;
  /**
   * Optional click handler for the button.
   * Maps to `onPress` for react-aria
   */
  onClick?: (e: PressEvent) => void;
  /**
   * Renders content before the button text. E.g; an Icon or Avatar
   */
  renderBeforeText?: (props: Props) => ReactNode;
  /**
   * The text content to render within the button.
   */
  text: string;
};

const tooltipClassName = createUniqueClassName('MoreButton', 'tooltip');
const tooltipSelector = `.${tooltipClassName}`;

const navIconClassName = createUniqueClassName('MoreButton', 'navIcon');
const navIconSelector = `.${navIconClassName}`;

const Styles = labelStyles('MoreButton', {
  root: (props: Pick<Props, 'isCollapsed'>) => [
    {
      font: `var(--zds-body-1, 400 14px/24px ${Typography.base.fontFamily})`,
      color: 'var(--zds-text-default, #413735)',
      display: 'flex',
      position: 'relative',
      minHeight: 'var(--zds-size-36, 36px)',
      padding: 'var(--zds-space-0, 0px) var(--zds-space-8, 8px)',
      borderRadius: 4,
      whiteSpace: 'nowrap',
      width: '100%',
      transition: `0s`,
      textDecoration: 'none',
      outline: 'solid 2px transparent',
      // This is the outline that renders around the button when
      // its focused. Its a pseudo element to preserve transparency;
      // between the edge of the button and the edge of the outline.
      // Its defined here vs only in ':focus' so that it can transition
      // between states.
      '&::before': {
        content: '""',
        position: 'absolute',
        pointerEvents: 'none',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        border: '2px solid transparent',
        borderRadius: 4,
      },
      '&:focus': {
        backgroundColor: 'var(--zds-background-weaker, #FFFDF9)',
      },
      '&:hover': {
        transition: 'none',
        backgroundColor: 'var(--zds-background-stronger, #F5F3EB)',
        outline: `solid 2px transparent`,
      },
      '&:focus-visible': {
        '&::before': {
          border: 'var(--zds-border-focus, 2px solid #695BE8)',
          top: '-2px',
          right: '-2px',
          bottom: '-2px',
          left: '-2px',
        },
      },
      [navIconSelector]: {
        color: 'var(--zds-text-default, #413735)',
      },
      [`&:hover ${navIconSelector}, &:focus ${navIconSelector}`]: {
        transition: 'none',
      },
    },
    props.isCollapsed &&
      withModifier('collapsed', {
        width: 'var(--zds-space-36, 36px)',
      }),
    {
      [tooltipSelector]: {
        opacity: 0,
        pointerEvents: 'none',
        transition: 'opacity 0.1s ease-in-out',
        '@media (hover: none)': {
          display: 'none',
        },
      },
      [`&:hover ${tooltipSelector}, &:focus ${tooltipSelector}`]: {
        opacity: 1,
        pointerEvents: 'auto',
      },
    },
  ],
  layout: {
    display: 'flex',
    gap: 'var(--zds-space-12, 12px)',
    alignItems: 'center',
    overflow: 'hidden',
    width: '100%',
  },
  text: {
    '&[aria-hidden="true"]': {
      opacity: 0,
    },
  },
});

/**
 * @private
 * Trigger for the MoreLinksMenu
 */
const MoreButton = ({
  text,
  icon,
  isCollapsed = false,
  onClick: onClickProp = undefined,
  renderBeforeText = (props: Props) =>
    props.icon && (
      <span className={navIconClassName}>
        <Icon isBlock={true} name={props.icon} size={20} />
      </span>
    ),
}: Props) => {
  const props = {
    text,
    icon,
    isCollapsed,
    onClick: onClickProp,
    renderBeforeText,
  } satisfies Props;

  const tooltip = isCollapsed && (
    <span className={tooltipClassName}>
      <Tooltip position="east">{text}</Tooltip>
    </span>
  );
  const onClick = (e: PressEvent) => {
    // Chrome is the only browser that causes an element to
    // be in a `:focus` state when it's clicked. That `:focus`
    // state causes `Tooltip` to undesirably render. The user
    // already clicked, so they don't need to see the tooltip.
    // To hide the tooltip, `blur` the node to cause it to no
    // longer have `:focus`. Tabbing location is still preserved.
    (e.target as HTMLElement).blur();
    if (onClickProp) {
      onClickProp(e);
    }
  };
  const beforeText = renderBeforeText && renderBeforeText(props);

  return (
    <Button
      css={Styles.root({ isCollapsed }, !!beforeText)}
      data-zds
      onPress={onClick}
      id="sidebar-more-button"
    >
      <span css={Styles.layout} data-zds>
        {beforeText}
        <span aria-hidden={isCollapsed} css={Styles.text} data-zds>
          {text}
        </span>
      </span>
      {tooltip}
    </Button>
  );
};

export default MoreButton;
